/**
 * SBIPublicHeader - Primary Public-Facing Header for SmallBiz Innovation
 *
 * This is the dedicated header component for www.smallbizinnovation.com
 * Unique to the www-sbi worker and independent of other workers.
 *
 * Features:
 * - Responsive floating header with glass morphism
 * - Hamburger menu for mobile (hidden by default)
 * - Horizontal navigation with nested dropdown menus
 * - Separate hover (desktop) / click (mobile) dropdown interactions
 * - 44px minimum touch targets for mobile (iOS HIG compliant)
 * - Smart dropdown positioning (prevents viewport overflow)
 * - Keyboard navigation support (Enter, Space, Escape)
 * - Focus trap for mobile menu
 * - Prominent "Get Started" CTA with offset styling
 * - Theme switcher integration
 * - Analytics event tracking
 * - Smooth animations and transitions
 * - Safe area support for notched devices
 * - SmallBiz Innovation branding
 */

import { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import { ChevronDown, ArrowRight, Menu, X, Search } from 'lucide-react';
import { ThemeSwitcher } from './ThemeSwitcher';
import { SBILogo } from './OptimizedImage';
import { useSessionAuth } from '../hooks/useSessionAuth';
import { AuthUserMenu } from './AuthUserMenu';

type NavLink = {
  name: string;
  href: string;
  description?: string;
};

type NavSection = {
  title: string;
  links: NavLink[];
};

type NavItem = {
  name: string;
  href: string;
  isActive: boolean;
};

interface SBIPublicHeaderProps {
  navItems: NavItem[];
  onNavigate: (href: string) => void;
}

// Helper function to send analytics events
function sendAnalyticsEvent(eventType: string, data: Record<string, unknown>) {
  // DISABLED: Analytics handled by Cloudflare Zaraz, PostHog, and Google Analytics
  // Prevents 405 errors from non-existent /api/events/* endpoints
  // UI interactions are automatically tracked by PostHog session replay and Zaraz
  void eventType;
  void data;
  return;
}

export function SBIPublicHeader({ navItems, onNavigate }: SBIPublicHeaderProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === 'undefined') {
      return false;
    }

    return document.documentElement.classList.contains('dark');
  });

  // Check authentication state
  const { isAuthenticated, user, isLoading: authLoading } = useSessionAuth();

  // Navigation sections with nested links
  const navSections: NavSection[] = [
    {
      title: 'Company',
      links: [
        { name: 'About', href: '/about' },
        { name: 'Services', href: '/services' },
        { name: 'Testimonials', href: '/testimonials', description: 'Client wins & reviews' },
        { name: 'Portfolio', href: '/portfolio', description: 'Work samples & case studies' },
        { name: 'Contact', href: '/contact' },
      ],
    },
    {
      title: 'Services',
      links: [
        {
          name: 'SmallBiz Programs',
          href: '/programs',
          description: 'Ongoing support & strategy',
        },
        { name: 'Support Desk', href: '/support-desk', description: 'On-demand help tickets' },
        { name: 'On-Demand Support', href: '/on-demand-support', description: 'Hourly consulting' },
        {
          name: 'Virtual Strategy Sessions',
          href: '/virtual-strategy-sessions',
          description: '1-on-1 strategy',
        },
        { name: 'Free Consults', href: '/free-consults', description: 'Book a free session' },
        { name: 'Pricing', href: '/pricing', description: 'Plans & pricing' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { name: 'Blog', href: '/blog' },
        { name: 'Glossary', href: '/glossary' },
        { name: 'Toolbox', href: '/toolbox' },
        { name: 'Updates', href: '/updates' },
        { name: 'Freebies', href: '/freebies' },
        { name: 'Newsletter', href: '/newsletter', description: 'Monthly updates' },
      ],
    },
    {
      title: 'Expertise',
      links: [
        { name: 'Marketing', href: '/marketing' },
        { name: 'Sales', href: '/sales' },
        { name: 'Automation', href: '/automation' },
        { name: 'Finance', href: '/finance' },
        { name: 'Leadership', href: '/leadership' },
        { name: 'Operations', href: '/operations' },
        { name: 'Strategy', href: '/strategy' },
      ],
    },
  ];

  // Desktop detection for hover interactions
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsDesktop(e.matches);
      // Close mobile menu when switching to desktop
      if (e.matches) {
        setMobileMenuOpen(false);
      }
    };

    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll-lock: runs synchronously before paint to avoid race with handleNavClick reset.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    if (mobileMenuOpen) {
      document.body.classList.add('sbi-mobile-menu-open');
    } else {
      document.body.classList.remove('sbi-mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('sbi-mobile-menu-open');
    };
  }, [mobileMenuOpen]);

  // Focus trap for mobile menu (keyboard accessibility only — no overflow management here)
  useEffect(() => {
    if (!mobileMenuOpen || typeof document === 'undefined') return;

    const focusableElements = mobileMenuRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [mobileMenuOpen]);

  // Theme detection
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const root = document.documentElement;

    const updateThemeState = () => {
      setIsDark(root.classList.contains('dark'));
    };

    updateThemeState();

    const observer = new MutationObserver(() => updateThemeState());
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = () => {
      if (!window.localStorage.getItem('sbi-theme')) {
        updateThemeState();
      }
    };

    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  const handleNavClick = (href: string, itemName: string) => {
    sendAnalyticsEvent('ui-event', {
      component: 'sbi_public_header',
      action: 'nav_click',
      target: href,
      label: itemName,
    });

    // Close menus and navigate
    setActiveDropdown(null);
    setMobileMenuOpen(false);

    // Use setTimeout to ensure state updates complete before navigation
    setTimeout(() => {
      onNavigate(href);
    }, 0);
  };

  const handleDropdownToggle = (sectionTitle: string) => {
    if (isDesktop) return; // Desktop uses hover
    setActiveDropdown(activeDropdown === sectionTitle ? null : sectionTitle);
  };

  const handleDropdownHover = useCallback(
    (sectionTitle: string | null) => {
      if (!isDesktop) return; // Mobile uses click

      // Clear existing timeout (ref — no re-render on clear)
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (sectionTitle === null) {
        // Delay closing to prevent flickering
        hoverTimeoutRef.current = setTimeout(() => {
          setActiveDropdown(null);
          hoverTimeoutRef.current = null;
        }, 150);
      } else {
        setActiveDropdown(sectionTitle);
      }
    },
    [isDesktop]
  );

  const handleKeyboardNav = (e: React.KeyboardEvent, sectionTitle: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDropdownToggle(sectionTitle);
    } else if (e.key === 'Escape') {
      setActiveDropdown(null);
    }
  };

  const getDropdownPosition = (index: number, totalSections: number) => {
    // Smart positioning: if dropdown is in the right half, align right
    if (index >= Math.floor(totalSections / 2)) {
      return 'right-0';
    }
    return 'left-0';
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-40 max-w-7xl xl:max-w-[1400px] mx-auto px-2 sm:px-4 pt-2 safe-area-top">
      <header>
        <div className="glass-morphism sbi-shadow-md rounded-2xl px-4 sm:px-6 py-2.5 sm:py-3">
          <div className="flex items-center justify-between gap-3 sm:gap-6">
            {/* Left Side: Hamburger + Logo + Brand (mobile menu toggle on mobile) */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Menu Toggle - Hamburger on left */}
              <button
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                className="md:hidden p-3 min-h-[44px] min-w-[44px] text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>

              {/* Logo + Brand Name - Always navigates home */}
              <button
                onClick={() => handleNavClick('/', 'Logo')}
                className="flex items-center space-x-2 sm:space-x-3 shrink-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg"
                aria-label="SmallBiz Innovation Home"
              >
                <div className="flex items-center gap-2 sm:gap-3 px-1 py-1 animate-pulse-subtle">
                  <SBILogo size={32} className="w-8 h-8 sm:w-10 sm:h-10 cursor-pointer" />
                  <span
                    className="font-bold text-sm sm:text-base text-foreground"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    <span className="hidden lg:inline">SmallBiz Innovation</span>
                    <span className="lg:hidden">SBI</span>
                  </span>
                </div>
              </button>
            </div>

            {/* Desktop Navigation with Dropdowns */}
            <nav
              ref={dropdownRef}
              className="hidden md:flex items-center gap-1 lg:gap-2 flex-1 justify-center"
              aria-label="Main navigation"
            >
              {/* Main navigation items */}
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item.href, item.name)}
                  className={`text-sm font-medium transition-all duration-200 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card rounded px-3 py-2 ${
                    item.isActive
                      ? 'text-primary bg-primary/10'
                      : 'text-foreground/80 hover:bg-primary/5'
                  }`}
                  aria-current={item.isActive ? 'page' : undefined}
                >
                  {item.name}
                </button>
              ))}

              {/* Dropdown sections */}
              {navSections.map((section, index) => (
                <div
                  key={section.title}
                  className="relative"
                  onMouseEnter={isDesktop ? () => handleDropdownHover(section.title) : undefined}
                  onMouseLeave={isDesktop ? () => handleDropdownHover(null) : undefined}
                >
                  <button
                    onClick={() => handleDropdownToggle(section.title)}
                    onKeyDown={(e) => handleKeyboardNav(e, section.title)}
                    className={`text-sm font-medium transition-all duration-200 flex items-center gap-1 px-3 py-2 rounded focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                      activeDropdown === section.title
                        ? 'text-primary bg-primary/10'
                        : 'text-foreground/80 hover:text-primary hover:bg-primary/5'
                    }`}
                    aria-expanded={activeDropdown === section.title}
                    aria-haspopup="true"
                  >
                    {section.title}
                    <ChevronDown
                      className={`w-3 h-3 transition-transform duration-200 ${
                        activeDropdown === section.title ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {activeDropdown === section.title && (
                    <div
                      className={`absolute top-full ${getDropdownPosition(index, navSections.length)} mt-2 min-w-[180px] z-50 animate-in fade-in-0 zoom-in-95 duration-200`}
                      role="menu"
                    >
                      <div className="glass-morphism border border-border rounded-xl shadow-xl py-2">
                        {section.links.map((link) => (
                          <button
                            key={link.href}
                            onClick={() => handleNavClick(link.href, link.name)}
                            className="block w-full text-left px-4 py-2.5 text-sm text-foreground/80 hover:text-primary hover:bg-primary/5 transition-all duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset rounded-md mx-1"
                            role="menuitem"
                          >
                            {link.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side: Search + Theme Switcher + Get Started CTA */}
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              {/* AI Search Button */}
              <button
                onClick={() => handleNavClick('/search', 'AI Search Header')}
                className="p-2.5 min-h-[44px] min-w-[44px] text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Open AI Search"
                title="Search (Cmd+K)"
              >
                <Search className="w-5 h-5" />
              </button>

              <ThemeSwitcher />

              {/* Authenticated User Menu OR Get Started CTA */}
              {isAuthenticated && user && !authLoading ? (
                <AuthUserMenu user={user} />
              ) : (
                <>
                  {/* Get Started CTA Button - Yellow with offset angle, shadow, and pulse */}
                  <button
                    onClick={() => handleNavClick('/get-started', 'Get Started CTA')}
                    className="hidden sm:flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all duration-300 transform hover:scale-110 active:scale-95 skew-x-[-2deg] hover:skew-x-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 animate-pulse-subtle"
                    style={{
                      fontFamily: 'var(--font-heading)',
                      background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                      color: '#1F2937',
                      boxShadow:
                        '0 8px 16px -4px rgba(245, 158, 11, 0.4), 0 4px 8px -2px rgba(0, 0, 0, 0.1)',
                      border: '2px solid rgba(251, 191, 36, 0.3)',
                      zIndex: 60,
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        '0 12px 24px -6px rgba(245, 158, 11, 0.6), 0 6px 12px -3px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        '0 8px 16px -4px rgba(245, 158, 11, 0.4), 0 4px 8px -2px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    Get Started
                  </button>

                  {/* Mobile Get Started Button (only when menu closed) */}
                  {!mobileMenuOpen && (
                    <button
                      onClick={() => handleNavClick('/get-started', 'Get Started CTA Mobile')}
                      className="sm:hidden flex items-center gap-1 px-4 py-3 min-h-[44px] rounded-lg font-bold text-xs transition-all transform active:scale-95 focus-visible:outline-hidden focus-visible:ring-2"
                      style={{
                        background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                        color: '#1F2937',
                        boxShadow: '0 4px 8px -2px rgba(245, 158, 11, 0.4)',
                        border: '1px solid rgba(251, 191, 36, 0.3)',
                      }}
                    >
                      Start
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Mobile Navigation Accordion */}
          {mobileMenuOpen && (
            <nav
              ref={mobileMenuRef}
              className="md:hidden mt-4 pt-4 border-t border-border/50 animate-in slide-in-from-top-4 duration-300 relative z-[1000] pointer-events-auto"
              aria-label="Mobile navigation"
              style={{ isolation: 'isolate' }}
            >
              <div className="flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
                {/* Main nav items */}
                {navItems.map((item) => (
                  <button
                    key={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavClick(item.href, item.name);
                    }}
                    className={`text-left px-4 py-3.5 rounded-lg text-sm font-medium transition-all duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset pointer-events-auto relative z-10 ${
                      item.isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/80 hover:bg-primary/5 hover:text-primary active:bg-primary/10'
                    }`}
                    aria-current={item.isActive ? 'page' : undefined}
                    type="button"
                  >
                    {item.name}
                  </button>
                ))}

                {/* Dropdown sections */}
                {navSections.map((section) => (
                  <div key={section.title} className="border-t border-border/30 pt-2 mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDropdownToggle(section.title);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDropdownToggle(section.title);
                      }}
                      onKeyDown={(e) => handleKeyboardNav(e, section.title)}
                      className={`w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium transition-all duration-150 rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
                        activeDropdown === section.title
                          ? 'text-primary bg-primary/5'
                          : 'text-foreground/70 hover:text-foreground hover:bg-primary/5'
                      }`}
                      aria-expanded={activeDropdown === section.title}
                      type="button"
                      style={{ touchAction: 'manipulation' }}
                    >
                      {section.title}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          activeDropdown === section.title ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {activeDropdown === section.title && (
                      <div className="mt-1 pl-6 animate-in slide-in-from-top-2 duration-200">
                        {section.links.map((link) => (
                          <button
                            key={link.href}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleNavClick(link.href, link.name);
                            }}
                            onTouchEnd={(e) => {
                              // Fire navigation immediately on touchend so iOS taps after
                              // scrolling the menu are always registered. preventDefault
                              // stops the browser synthesising a second click event.
                              e.preventDefault();
                              handleNavClick(link.href, link.name);
                            }}
                            className="block w-full text-left px-4 py-3.5 text-sm text-foreground/60 hover:text-foreground hover:bg-primary/5 rounded-lg transition-all duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset active:bg-primary/10 pointer-events-auto relative z-[1001] cursor-pointer"
                            type="button"
                            style={{
                              touchAction: 'manipulation',
                            }}
                          >
                            {link.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Mobile AI Search Button */}
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleNavClick('/search', 'AI Search Mobile Menu');
                  }}
                  className="mt-2 w-full flex items-center gap-3 px-4 py-3.5 rounded-lg text-sm font-medium text-foreground/80 hover:text-primary hover:bg-primary/5 transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
                  type="button"
                >
                  <Search className="w-4 h-4" />
                  AI Search
                  <span className="ml-auto text-xs text-muted-foreground">⌘K</span>
                </button>

                {/* Mobile Menu: Auth User Menu OR Get Started */}
                <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                  {isAuthenticated && user && !authLoading ? (
                    <div className="w-full flex justify-center">
                      <AuthUserMenu user={user} />
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavClick('/get-started', 'Get Started Mobile Menu')}
                      className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-3.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-foreground"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      Get Started
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

    </div>
  );
}
